/**
 * Electrical takeoff catalog — the bridge between the NEC conduit/wire tables,
 * the gear list and the job estimate.
 *
 * Every item id here is also its material-catalog key, so a price edited in the
 * Shop flows straight into the estimate. Hours are seed field units for a
 * journeyman + apprentice crew and are editable per line in the estimator.
 */

import {
  AWG_SIZES,
  DEVICES,
  DEVICE_KEYS,
  RACEWAYS,
  TRADE_SIZES,
  WIRE_TYPES,
  type AwgSize,
  type InsulationId,
  type RacewayId,
  type TradeSize,
} from "@/lib/electrical-data";
import { catalogKeys } from "@/lib/material-catalog";

export type ElectricalSectionId = "conduit" | "wire" | "gear" | "devices";

export const ELECTRICAL_SECTIONS: Array<{ id: ElectricalSectionId; label: string }> = [
  { id: "conduit", label: "Conduit & raceway" },
  { id: "wire", label: "Wire & cable" },
  { id: "gear", label: "Panels & gear" },
  { id: "devices", label: "Devices, boxes & fixtures" },
];

export interface ElectricalTakeoffItem {
  /** material-catalog key, e.g. econd:emt:3/4" */
  id: string;
  section: ElectricalSectionId;
  label: string;
  size: string;
  /** ft for raceway and conductors, ea for gear and devices */
  unit: "ft" | "ea";
  /** crew-hours per unit installed */
  hours: number;
}

function conduitItems(): ElectricalTakeoffItem[] {
  return (Object.keys(RACEWAYS) as RacewayId[]).flatMap((r) => {
    const race = RACEWAYS[r];
    return TRADE_SIZES.filter((sz) => race.price[sz] != null).map((sz: TradeSize) => ({
      id: catalogKeys.conduit(r, sz),
      section: "conduit" as const,
      label: race.label,
      size: sz,
      unit: "ft" as const,
      hours: race.hrs[sz] ?? 0,
    }));
  });
}

function wireItems(): ElectricalTakeoffItem[] {
  return (Object.keys(WIRE_TYPES) as InsulationId[]).flatMap((i) => {
    const w = WIRE_TYPES[i];
    return AWG_SIZES.map((a: AwgSize) => ({
      id: catalogKeys.wire(i, a),
      section: "wire" as const,
      label: w.label,
      size: `#${a}`,
      unit: "ft" as const,
      hours: w.hrs[a],
    }));
  });
}

function deviceItems(): ElectricalTakeoffItem[] {
  return DEVICE_KEYS.map((k) => {
    const d = DEVICES[k];
    return {
      id: catalogKeys.device(k),
      section: (d.gear ? "gear" : "devices") as ElectricalSectionId,
      label: d.label,
      size: "—",
      unit: "ea" as const,
      hours: d.hrs,
    };
  });
}

const ALL: ElectricalTakeoffItem[] = [...conduitItems(), ...wireItems(), ...deviceItems()];

const BY_ID = new Map(ALL.map((i) => [i.id, i]));

export const ELECTRICAL_ITEMS = ALL;

export const electricalItems = (section: ElectricalSectionId) =>
  ALL.filter((i) => i.section === section);

export const getElectricalItem = (id: string) => BY_ID.get(id) ?? null;

export const electricalUnitLabel = (unit: "ft" | "ea") => (unit === "ft" ? "ft" : "ea");
